/**
 * 套餐模型（客户端，字段与服务器 packages 表一一对应）。
 * 定价页消费；运行时优先拉 GET /api/v2/packages（后台改了即时生效），
 * 拉取失败/未配库时回退内置 PACKAGES（与 schema seed 一致，便于离线预览）。
 * 表述：金额 + 加送%，全程不出现积分数（对齐"百分比消耗"方针）。
 */

export type PackageKind = 'personal' | 'team';
export type PackageLevel = 'standard' | 'advanced' | 'premium' | 'deluxe';
export type Period = 'year' | 'quarter' | 'month';

export interface Package {
  id: number;
  kind: PackageKind;
  level: PackageLevel;
  code: string;
  name: string;
  priceYearCents: number;
  priceQuarterCents: number;
  priceMonthCents: number;
  bonusYearPct: number;
  bonusQuarterPct: number;
  bonusMonthPct: number;
  perSeat: boolean;
  seatsMin: number;
  seatsMax: number;
  storageGb: number;
  quotaTier: string;
  features: string[];
  active: boolean;
  sort: number;
}

export const PERIODS: { k: Period; label: string; tag: string }[] = [
  { k: 'year', label: '按年购买', tag: '最划算' },
  { k: 'quarter', label: '按季购买', tag: '较省' },
  { k: 'month', label: '按月购买', tag: '灵活' },
];

export function priceCents(p: Package, period: Period): number {
  if (period === 'year') return p.priceYearCents;
  if (period === 'quarter') return p.priceQuarterCents;
  return p.priceMonthCents;
}
export function bonusPct(p: Package, period: Period): number {
  if (period === 'year') return p.bonusYearPct;
  if (period === 'quarter') return p.bonusQuarterPct;
  return p.bonusMonthPct;
}
export function formatYuan(cents: number): string {
  return `¥${(cents / 100).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`;
}

/** 服务器行（snake_case + features 可能为 JSON 字符串）→ 客户端 Package。 */
export function mapPackageRow(r: Record<string, unknown>): Package {
  let features: string[] = [];
  const raw = r['features'];
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) features = parsed.map(String);
    } catch {
      features = [];
    }
  } else if (Array.isArray(raw)) {
    features = raw.map(String);
  }
  return {
    id: Number(r['id'] ?? 0),
    kind: (r['kind'] as PackageKind) ?? 'personal',
    level: (r['level'] as PackageLevel) ?? 'standard',
    code: String(r['code'] ?? ''),
    name: String(r['name'] ?? ''),
    priceYearCents: Number(r['price_year_cents'] ?? 0),
    priceQuarterCents: Number(r['price_quarter_cents'] ?? 0),
    priceMonthCents: Number(r['price_month_cents'] ?? 0),
    bonusYearPct: Number(r['bonus_year_pct'] ?? 0),
    bonusQuarterPct: Number(r['bonus_quarter_pct'] ?? 0),
    bonusMonthPct: Number(r['bonus_month_pct'] ?? 0),
    perSeat: Number(r['per_seat'] ?? 0) === 1,
    seatsMin: Number(r['seats_min'] ?? 0),
    seatsMax: Number(r['seats_max'] ?? 0),
    storageGb: Number(r['storage_gb'] ?? 0),
    quotaTier: String(r['quota_tier'] ?? 'basic'),
    features,
    active: Number(r['active'] ?? 1) === 1,
    sort: Number(r['sort'] ?? 0),
  };
}

const PERSONAL_FEATURES = [
  '全能视频Agent', '3D导演台', '脚本策划', '智能分镜', '多宫格生成', '宫格切分',
  '镜头聚焦', '多模态主体库', '视频剪辑', '去品牌水印', '题材技能模板库',
];
function teamFeatures(gb: number): string[] {
  return ['含个人版全部', '多人画布协作', '团队共享资产库', '团队席位管理', '成员用量管控', '项目权限管理', `团队素材云空间 ${gb}GB`];
}

/** 内置兜底（与 schema seed 一致）。 */
export const PACKAGES: Package[] = [
  { id: 1, kind: 'personal', level: 'standard', code: 'p-standard', name: '标准版', priceYearCents: 56900, priceQuarterCents: 17900, priceMonthCents: 6600, bonusYearPct: 45, bonusQuarterPct: 25, bonusMonthPct: 10, perSeat: false, seatsMin: 0, seatsMax: 0, storageGb: 0, quotaTier: 'basic', features: PERSONAL_FEATURES, active: true, sort: 1 },
  { id: 2, kind: 'personal', level: 'advanced', code: 'p-advanced', name: '进阶版', priceYearCents: 119900, priceQuarterCents: 49900, priceMonthCents: 19900, bonusYearPct: 45, bonusQuarterPct: 25, bonusMonthPct: 10, perSeat: false, seatsMin: 0, seatsMax: 0, storageGb: 0, quotaTier: 'generous', features: PERSONAL_FEATURES, active: true, sort: 2 },
  { id: 3, kind: 'personal', level: 'premium', code: 'p-premium', name: '高级版', priceYearCents: 399900, priceQuarterCents: 159900, priceMonthCents: 64900, bonusYearPct: 45, bonusQuarterPct: 25, bonusMonthPct: 10, perSeat: false, seatsMin: 0, seatsMax: 0, storageGb: 0, quotaTier: 'ample', features: PERSONAL_FEATURES, active: true, sort: 3 },
  { id: 4, kind: 'personal', level: 'deluxe', code: 'p-deluxe', name: '旗舰版', priceYearCents: 699900, priceQuarterCents: 269900, priceMonthCents: 119900, bonusYearPct: 45, bonusQuarterPct: 25, bonusMonthPct: 10, perSeat: false, seatsMin: 0, seatsMax: 0, storageGb: 0, quotaTier: 'unlimited', features: PERSONAL_FEATURES, active: true, sort: 4 },
  { id: 5, kind: 'team', level: 'standard', code: 't-standard', name: '团队 标准版', priceYearCents: 68900, priceQuarterCents: 19900, priceMonthCents: 7900, bonusYearPct: 42, bonusQuarterPct: 22, bonusMonthPct: 8, perSeat: true, seatsMin: 2, seatsMax: 20, storageGb: 60, quotaTier: 'basic', features: teamFeatures(60), active: true, sort: 5 },
  { id: 6, kind: 'team', level: 'advanced', code: 't-advanced', name: '团队 进阶版', priceYearCents: 139900, priceQuarterCents: 59900, priceMonthCents: 22900, bonusYearPct: 42, bonusQuarterPct: 22, bonusMonthPct: 8, perSeat: true, seatsMin: 2, seatsMax: 20, storageGb: 80, quotaTier: 'generous', features: teamFeatures(80), active: true, sort: 6 },
  { id: 7, kind: 'team', level: 'premium', code: 't-premium', name: '团队 高级版', priceYearCents: 459900, priceQuarterCents: 189900, priceMonthCents: 72900, bonusYearPct: 42, bonusQuarterPct: 22, bonusMonthPct: 8, perSeat: true, seatsMin: 2, seatsMax: 20, storageGb: 100, quotaTier: 'ample', features: teamFeatures(100), active: true, sort: 7 },
  { id: 8, kind: 'team', level: 'deluxe', code: 't-deluxe', name: '团队 旗舰版', priceYearCents: 799900, priceQuarterCents: 319900, priceMonthCents: 139900, bonusYearPct: 42, bonusQuarterPct: 22, bonusMonthPct: 8, perSeat: true, seatsMin: 2, seatsMax: 20, storageGb: 120, quotaTier: 'unlimited', features: teamFeatures(120), active: true, sort: 8 },
];
